import './CategoryDataGrid.scss';
import { BaseWidget, WidgetAction } from 'models/WidgetModel';
import { SortingState, PaginationState, ColumnHeader } from '@cx/ui/components/DataGrid';
import React, { useEffect, useState } from 'react';
import { smileyIcon, neutralIcon, frownIcon, appAssets } from 'constants/assets';
import HorizontalSingleSentimentStackedBarChart from 'components/Charts/HorizontalSingleSentimentStackedBarChart/HorizontalSingleSentimentStackedBarChart';
import Tippy from '@tippyjs/react';
import { useWidgetContext } from 'contexts/WidgetProvider';
import { CategoryStyledDataGrid } from 'features/DataGrid/DataGridStyling';

interface CategoryDataGridProps extends BaseWidget {
  response: any;
  options: any;
  colors?: string[];
  dataOnClick?: any;
}
const Circle = appAssets.icons.CIRCLE;
const TrendingFlat = appAssets.icons.TRENDING_FLAT;
const TrendingUp = appAssets.icons.TRENDING_UP;
const TrendingDown = appAssets.icons.TRENDING_DOWN;
const FALLBACK_TEXT = 'N/A';

const CategoryDataGrid: React.FC<CategoryDataGridProps> = ({
  response: defaultResponse,
  options,
  dataOnClick,
  ...props
}) => {
  const [data, setData] = useState<any>([]);
  const [columns, setColumns] = useState<any>([]);
  const [serieColor, setSerieColor] = useState<any>([]);
  const [showAll, setShowAll] = useState<boolean>(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const { action, setState } = useWidgetContext();

  const hasDisplayValue = (value: any) =>
    value !== null && value !== undefined && !(typeof value === 'string' && value.trim() === '');

  const formatDisplayValue = (value: any) => {
    if (!hasDisplayValue(value)) return FALLBACK_TEXT;

    const rawValue = typeof value === 'string' ? value.trim() : value;
    const numericValue = Number(rawValue);

    if (Number.isNaN(numericValue)) {
      return rawValue;
    }

    const roundedToTwoDecimals = Math.round(numericValue * 100) / 100;

    if (Number.isInteger(roundedToTwoDecimals)) {
      return roundedToTwoDecimals;
    }

    return roundedToTwoDecimals.toFixed(2);
  };

  useEffect(() => {
    if (action === WidgetAction.EXPANDABLE) {
      setShowAll(!showAll);
      setState({ action: WidgetAction.NONE });
    }
  }, [action]);

  useEffect(() => {
    const tableData = defaultResponse?.data;
    if (tableData) {
      const displayedData = showAll ? tableData?.data : tableData?.data?.slice(0, 5);
      setData(displayedData);
      setColumns(tableData?.columns);
    }
  }, [defaultResponse, showAll]);

  useEffect(() => {
    if (data) {
      const colorsArray = props?.colors?.map((item: any, index: any) => {
        return { id: data[index]?.id, color: item };
      });
      setSerieColor(colorsArray);
    }
  }, [data]);

  const handleClick = (row: any) => {
    try {
      dataOnClick(row);
    } catch (error: any) {
      console.error(`${error.message}, be sure you are running from CX application`);
    }
  };

  const showTrend = (delta: number) => {
    if (delta == 0 || delta == null) {
      return (
        <div className="pck-child-div">
          <TrendingFlat className="biggest-yellow-arrow"></TrendingFlat>
        </div>
      );
    } else if (delta > 0) {
      return (
        <div className="pck-child-div">
          <TrendingUp className="biggest-green-arrow"></TrendingUp>
        </div>
      );
    } else if (delta < 0) {
      return (
        <div className="pck-child-div">
          <TrendingDown className="biggest-red-arrow"></TrendingDown>
        </div>
      );
    }
  };

  const showFace = (delta: number) => {
    if (delta == 0 || delta == null) {
      return (
        <div className="pck-child-div face-container">
          <img src={neutralIcon} className="yellow-face" />
        </div>
      );
    } else if (delta > 0) {
      return (
        <div className="pck-child-div face-container">
          <img src={smileyIcon} className="green-face" />
        </div>
      );
    } else if (delta < 0) {
      return (
        <div className="pck-child-div face-container">
          <img src={frownIcon} className="red-face" />
        </div>
      );
    }
  };

  const getRowContent = (column: any, row: any, l1: any) => {
    const cellValue = row[column?.Field];
    const hasValue = hasDisplayValue(cellValue);
    const formattedValue = formatDisplayValue(cellValue);

    if (column?.ColType === 'Trend') {
      return (
        <div className="pck-parent">
          <div className="pck-value-width">
            <span>
              {formattedValue}
              {column.IsPercentage && hasValue ? ' %' : ''}
            </span>
          </div>
          {hasValue ? showTrend(Number(cellValue)) : ''}
        </div>
      );
    }
    if (column?.ColType === 'Sentiment') {
      return (
        <div className="pck-parent">
          <div className="pck-value-width">
            <span>{formattedValue}</span>
          </div>
          {hasValue ? showFace(Number(cellValue)) : ''}
        </div>
      );
    }
    if (column?.ColType === 'HSB') {
      const sentiments = ['Negative', 'Neutral', 'Positive'];
      const values = row[column.Field]?.split(';').map((item: any, index: number) => {
        return {
          label: sentiments[index],
          value: item,
        };
      });
      const series = { data: { series: values, isPercentage: true } };
      return (
        <HorizontalSingleSentimentStackedBarChart
          colors={[]}
          options={options}
          {...props}
          response={series}
        />
      );
    }
    if (
      column.ColType === 'UDL1CategoryLink' ||
      column.ColType === 'UDCategoryLink' ||
      column.ColType === 'UDL2CategoryLink'
    ) {
      const circleColor = serieColor.find((item: any) => item.id === row.id);
      const primaryValue = formatDisplayValue(row[column?.Field]);
      const secondaryValue = formatDisplayValue(row[l1?.Field]);
      return (
        <div className="pck-parent">
          <div className="pck-child-div">
            {circleColor?.color ? (
              <Circle fill={circleColor?.color} className="circle" />
            ) : (
              <div className="circle"></div>
            )}
          </div>
          <div className="pck-child-div pck-first-colum-width">
            <Tippy
              content={
                <div title={String(primaryValue)}>
                  <div className="tooltip-title">{primaryValue}</div>
                </div>
              }
            >
              <div className={`${column?.IsLink ? 'pck-table-link' : ''}  pck-data-grid-ellipsis`}>
                <span
                  onClick={() => handleClick(row)}
                  data-tag={column?.IsLink ? 'allowRowEvents' : ''}
                >
                  {primaryValue}
                </span>
              </div>
            </Tippy>
            <Tippy
              content={
                <div title={String(secondaryValue)}>
                  <div className="tooltip-title">{secondaryValue}</div>
                </div>
              }
            >
              <div data-tag="allowRowEvents pck-first-colum-width pck-data-grid-ellipsis">
                <span className="base-size-span">{secondaryValue}</span>
              </div>
            </Tippy>
          </div>
        </div>
      );
    }
    return (
      <div>
        <div className="tableLink">
          <span>{formattedValue}</span>
        </div>
      </div>
    );
  };

  const getColumns = () => {
    const l1 = columns?.find((item: any) => item?.ColName === 'l1categoryname');
    const filterColumn = columns?.filter((item: any) => item?.ColType !== 'Hidden');
    if (!filterColumn) return [];
    return filterColumn?.map((col: any) => {
      return {
        id: col?.Field,
        accessorKey: col?.Field,
        header: ({ column }: { column: any }) => (
          <ColumnHeader column={column} title={col?.DisplayName} />
        ),
        filterFn: 'includesString',
        cell: ({ cell }: { cell: any }) => getRowContent(col, cell.row.original, l1),
        meta: {
          columnStyle: {
            width: '20%',
          },
        },
      };
    });
  };

  return (
    <div className={`pck-category-table-container`}>
      <div className={`data-grid ${showAll && 'sc-container'} fixed-header`}>
        <CategoryStyledDataGrid
          className="data-grid-table"
          columns={getColumns() ?? []}
          data={data ?? []}
          pagination={paginationState}
          paginationOptions={{
            onPaginationChange: setPaginationState,
            manualPagination: false,
            rowCount: data.length,
          }}
          enablePagination={false}
          sorting={sorting}
          sortingOptions={{
            enableSorting: true,
            onSortingChange: (sortingValue: SortingState) => {
              setSorting(sortingValue);
            },
            manualSorting: false,
          }}
        />
      </div>
    </div>
  );
};

export default CategoryDataGrid;
